import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";
import { FormGroup, FormsModule } from "@angular/forms";
import { Table, TableModule } from "primeng/table";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { v4 as uuidv4 } from "uuid";
import { ReleasedProductVariants } from "../../../core/models/shared/releasedProductVariants";
import { ReleasedProduct } from "../../../core/models/shared/releaseProduct";
import { OrderSummaryService } from "../../../core/services/order-summary.service";
import { SamplepoListingService } from "../../../core/services/samplepo-listing.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { AccordionModule } from "primeng/accordion";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { CommonModule } from "@angular/common";
import { ExchangeRatesDto } from "../../../core/models/shared/exchangeRateDto";
import { PurchaseOrderLine } from "../../../core/models/samplePO/details/purchaseOrderLine";

import { PanelModule } from "primeng/panel";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@Component({
  selector: "jni-finished-goods-template-grid",
  templateUrl: "./finished-goods-template-grid.component.html",
  styleUrls: ["./finished-goods-template-grid.component.scss"],
  imports: [
    AccordionModule,
    TableModule,
    SelectModule,
    FormsModule,
    CommonModule,
    PanelModule,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
})
@UntilDestroy()
export class FinishedGoodsTemplateGridComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() selectedLineGroup = "";
  @Input() currencyConversion!: ExchangeRatesDto;
  @Input() site = "";
  @Input() warehouse = "";
  @Input() purchaseOrderNumber = "";
  @Input() owner = "";
  @Input() parent!: FormGroup;
  @Input() itemNumber = "";
  @Output() rowSaveEvent = new EventEmitter<PurchaseOrderLine>();
  @Output() copyLinesEvent = new EventEmitter<PurchaseOrderLine[]>();
  @Output() deleteLinesEvent = new EventEmitter<PurchaseOrderLine[]>();
  @Output() selectedLineGroupChangeEvent = new EventEmitter<string>();

  @Input() releasedProductsList: ReleasedProduct[] = [];
  @Input() allReleasedProductsList: ReleasedProduct[] = [];
  @Input() releasedProductsVariantsList: ReleasedProductVariants[] = [];
  @Input() allReleasedProductsVariantsList: ReleasedProductVariants[] = [];

  filteredFinishedGoodsList: PurchaseOrderLine[] = [];
  @Input() finishedGoodsList: PurchaseOrderLine[] = [];
  selectedGoods: PurchaseOrderLine[] = []; // Array to store selected items

  descriptionCharacterCond = false;

  deleteButtonStatus = false;

  dropdown = [];
  unit = ["ea", "cs"];

  netTotalPrice = "";
  netTotalPriceConverted = "";
  isoCurrencyCode = "";
  netReteAfterConversion = 0;
  netConvertToUsd = "";
  displayAddressAssignment = false;

  filteredColors: string[] = [];
  filteredSizes: string[] = [];
  filteredStyles: string[] = [];
  filteredConfig: string[] = [];

  @ViewChild("dt") table!: Table;

  constructor(
    private _orderSummaryService: OrderSummaryService,
    private _samplepoListingService: SamplepoListingService,
    private _toastr: JniToastrService,
    private _elementRef: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    // p-select v19 calls event.stopPropagation() on every Tab keydown, which
    // blocks pEditableColumn's built-in Tab-to-next-cell navigation. We
    // intercept Tab in the CAPTURE phase (before p-select's handler runs) and
    // manually trigger the click on the next .p-editable-column td, which
    // opens it for editing and auto-focuses its first input/textarea.
    this._elementRef.nativeElement.addEventListener(
      "keydown",
      this._onEditableCellTabCapture,
      true
    );
  }

  ngOnDestroy(): void {
    this._elementRef.nativeElement.removeEventListener(
      "keydown",
      this._onEditableCellTabCapture,
      true
    );
  }

  private _onEditableCellTabCapture = (event: KeyboardEvent): void => {
    if (event.key !== "Tab") return;
    const target = event.target as HTMLElement | null;
    // Only act when Tab happens inside a cell that is currently being edited
    const currentTd = target?.closest(
      "td.p-editable-column.p-cell-editing"
    ) as HTMLElement | null;
    if (!currentTd) return;

    const direction = event.shiftKey
      ? "previousElementSibling"
      : "nextElementSibling";
    let nextTd = currentTd[direction] as HTMLElement | null;
    while (nextTd && !nextTd.classList.contains("p-editable-column")) {
      nextTd = nextTd[direction] as HTMLElement | null;
    }
    if (!nextTd) return;

    event.preventDefault();
    event.stopPropagation();
    // Click the target cell to trigger pEditableColumn's onClick -> openCell(),
    // which opens the edit template and auto-focuses input/textarea/select.
    nextTd.click();
  };

  ngOnChanges(changes: SimpleChanges) {
    this.lineGroupChanges(changes);
    this.finishedGoodsListChanges(changes);
    this.selectedLineGroupChanges(changes);
    this.ownerChanges(changes);
  }

  lineGroupChanges(changes: SimpleChanges) {
    if (changes["selectedLineGroup"]) {
      this.filterFinishedGoodsLines();
    }
  }

  finishedGoodsListChanges(changes: SimpleChanges) {
    if (changes["finishedGoodsList"]) {
      this.filterFinishedGoodsLines();
    }
  }

  ownerChanges(changes: SimpleChanges) {
    if (changes["owner"]) {
      this.filterAllReleasedProducts();
    }
  }

  selectedLineGroupChanges(changes: SimpleChanges) {
    if (changes["selectedLineGroup"]) {
      if (
        !changes["selectedLineGroup"].firstChange &&
        changes["selectedLineGroup"].currentValue &&
        changes["selectedLineGroup"].previousValue
      ) {
        this.selectedLineGroupChangeEvent.emit(
          changes["selectedLineGroup"].previousValue
        );
      }
    }
  }

  filterAllReleasedProducts() {
    this.releasedProductsList = this.allReleasedProductsList?.filter(
      x => x.ownerD365 == this.owner
    );
  }

  filterFinishedGoodsLines() {
    this.selectedGoods = [];
    this.filteredFinishedGoodsList = this.finishedGoodsList.filter(
      x => x.lineGroupD365 == this.selectedLineGroup
    );

    this.setLineDetails();
  }

  hideDeleteLineButtons() {
    this.deleteButtonStatus = !this.deleteButtonStatus;
  }

  setLineDetails() {
    this.filteredFinishedGoodsList.forEach(z => {
      const product = this.releasedProductsList.find(
        y => y.itemNumberD365 === z.itemNumberD365
      );
      let variants = this.releasedProductsVariantsList.filter(
        x => x.itemIdD365 == z.itemNumberD365
      );

      if (z?.colorD365) {
        variants = variants.filter(x => x.colorD365 == z?.colorD365);
      }
      if (z?.sizeD365) {
        variants = variants.filter(x => x.sizeD365 == z?.sizeD365);
      }
      if (z?.styleD365) {
        variants = variants.filter(x => x.styleD365 == z?.styleD365);
      }
      if (z?.configD365) {
        variants = variants.filter(x => x.configD365 == z?.configD365);
      }

      z.productNameD365 = product?.nameD365 ?? "";
      z.physicalAvailableD365 = variants[0]?.physicalAvailableD365;
      z.physicalInventoryD365 = variants[0]?.physicalInventoryD365;
    });
  }

  getAllReleaseProduct() {
    this._orderSummaryService
      .getAllCategory()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.allReleasedProductsList = res.data?.filter(
            x => x.productTypeD365 !== "Service" && x.poStopped === "No"
          );
          this.releasedProductsList = this.allReleasedProductsList?.filter(
            x => x.ownerD365 == this.owner
          );
          this.setCategoryNameToLines();
          this.setLineDetails();
        },
      });
  }

  setCategoryNameToLines() {
    this.filteredFinishedGoodsList.forEach(x => {
      x.productNameD365 =
        this.releasedProductsList.find(
          y => y.itemNumberD365 == x.itemNumberD365
        )?.nameD365 ?? "";
    });
  }

  getAllVariants() {
    this._samplepoListingService
      .getAllVariants()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.releasedProductsVariantsList = res.data?.filter(
            x => x.poStopped === "No"
          );
          this.setLineDetails();
        },
      });
  }

  calculateQuantity(event: number, purchaseOrderLine: PurchaseOrderLine) {
    //const quantity = event;
    let quantity = event.toString().replace(/[^-0-9]/g, "");
    quantity = quantity.substring(0, 5);
    if (quantity) {
      purchaseOrderLine.orderedPurchaseQuantityD365 = parseInt(quantity);
      const netPrice = purchaseOrderLine.purchasePriceD365;
      if (netPrice) {
        //netPrice = netPrice.replace(/[^a-zA-Z0-9.]/g, '');
        const convertedNetPrice = purchaseOrderLine.netConversion;
        this.patchValueInTotalNet(
          netPrice.toString(),
          convertedNetPrice?.toString() ?? "",
          purchaseOrderLine.orderedPurchaseQuantityD365.toString(),
          purchaseOrderLine
        );
      }
    } else {
      purchaseOrderLine.orderedPurchaseQuantityD365 = parseInt(quantity);
    }
  }

  patchValueInTotalNet(
    net: string,
    convertedNet: string,
    lineQuantity: string,
    lineObject: PurchaseOrderLine
  ) {
    const quantity = lineQuantity;
    if (quantity) {
      const netprice = parseFloat(net) * parseInt(quantity);
      this.netTotalPrice = netprice.toString();
      lineObject.lineAmountD365 = parseFloat(this.netTotalPrice);
      const netPriceCon =
        parseFloat(convertedNet == "" ? "0" : convertedNet) *
        parseInt(quantity);
      this.netTotalPriceConverted = netPriceCon.toString();
      lineObject.netConversionTotal = parseFloat(
        this.netTotalPriceConverted ?? "0"
      );
    }
  }

  onNetConversion(event: string, lineObject: PurchaseOrderLine) {
    let netRate = event.replace(/[^0-9.]/g, "");
    const regExp = /^(\d+(\.\d+)?)$/;
    const revenue = regExp.test(netRate) ? "valid" : "invalid";
    if (revenue === "valid") {
      netRate = netRate.substring(0, 9);
      lineObject.purchasePriceD365 = parseFloat(netRate);
      if (netRate) {
        if (this.currencyConversion) {
          if (this.currencyConversion.Rate != 0) {
            this.netReteAfterConversion =
              parseFloat(netRate) * this.currencyConversion?.Rate;
            if (this.netReteAfterConversion.toString() != "") {
              this.netConvertToUsd = this.netReteAfterConversion.toString();
              lineObject.netConversion = parseFloat(
                this.netConvertToUsd ?? "0"
              );
            }
          }
        }
        this.patchValueInTotalNet(
          netRate,
          this.netReteAfterConversion.toString(),
          lineObject.orderedPurchaseQuantityD365?.toString() ?? "0",
          lineObject
        );
      }
    } else {
      lineObject.purchasePriceD365 = 0;
      lineObject.lineAmountD365 = 0;
      lineObject.netConversion = 0;
      lineObject.netConversionTotal = 0;
    }
  }

  getCurrencyConvertionCode(currency: string) {
    if (currency) {
      return currency;
    } else {
      return "USD";
    }
  }

  addNewService() {
    this.cancelAllEditRow();
    const object = {
      id: 0,
      lineRecIdD365: null,
      portalKeyD365: uuidv4(),
      parentD365: true,
      orderedPurchaseQuantityD365: 0,
      purchasePriceD365: 0,
      lineAmountD365: 0,
      mockSellD365: 0,
      mockSellTotalD365: 0,
      purchaseUnitSymbolD365: "ea",
    } as PurchaseOrderLine;
    this.filteredFinishedGoodsList.push(object);
    this.finishedGoodsList.push(object);
    this.table.initRowEdit(this.table.value[this.table.value?.length - 1]);
  }

  cancelAllEditRow() {
    this.table.value.forEach(x => {
      this.table.cancelRowEdit(x);
    });
  }

  itemSelected(
    itemNumber: SelectChangeEvent,
    purchaseOrderLine: PurchaseOrderLine
  ) {
    const itemNumberValue = itemNumber.value as string;
    this.filteredColors = [
      ...new Set(
        this.allReleasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.colorD365)
          .sort((a, b) => a.colorDisplayOrderD365 - b.colorDisplayOrderD365)
          .map(x => x.colorD365)
      ),
    ];
    this.filteredSizes = [
      ...new Set(
        this.allReleasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.sizeD365)
          .sort(
            (a, b) =>
              a.sizeGlobalDisplayOrderD365 - b.sizeGlobalDisplayOrderD365
          )
          .map(x => x.sizeD365)
      ),
    ];
    this.filteredConfig = [
      ...new Set(
        this.allReleasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.configD365)
          .sort((a, b) => a.configDisplayOrderD365 - b.configDisplayOrderD365)
          .map(x => x.configD365)
      ),
    ];
    this.filteredStyles = [
      ...new Set(
        this.allReleasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumberValue && x.styleD365)
          .sort((a, b) => a.styleDisplayOrderD365 - b.styleDisplayOrderD365)
          .map(x => x.styleD365)
      ),
    ];

    const releasedprod = this.releasedProductsList.find(
      x => x.itemNumberD365 == itemNumberValue
    );
    purchaseOrderLine.productNameD365 = releasedprod?.nameD365 ?? "";

    const color = this.filteredColors[0];
    const size = this.filteredSizes[0];
    const config = this.filteredConfig[0];
    const style = this.filteredStyles[0];

    purchaseOrderLine.colorD365 = color;
    purchaseOrderLine.sizeD365 = size;
    purchaseOrderLine.configD365 = config;
    purchaseOrderLine.styleD365 = style;

    let variants = this.allReleasedProductsVariantsList.filter(
      x => x.itemIdD365 == itemNumberValue
    );

    if (purchaseOrderLine?.colorD365) {
      variants = variants.filter(
        x => x.colorD365 == purchaseOrderLine?.colorD365
      );
    }
    if (purchaseOrderLine?.sizeD365) {
      variants = variants.filter(
        x => x.sizeD365 == purchaseOrderLine?.sizeD365
      );
    }
    if (purchaseOrderLine?.styleD365) {
      variants = variants.filter(
        x => x.styleD365 == purchaseOrderLine?.styleD365
      );
    }
    if (purchaseOrderLine?.configD365) {
      variants = variants.filter(
        x => x.configD365 == purchaseOrderLine?.configD365
      );
    }

    purchaseOrderLine.physicalAvailableD365 =
      variants[0]?.physicalAvailableD365;
    purchaseOrderLine.physicalInventoryD365 =
      variants[0]?.physicalInventoryD365;

    this.generateDescription(purchaseOrderLine);
  }

  descriptionCharacterCount(obj: string) {
    if (obj && obj?.length > 45) {
      this.descriptionCharacterCond = true;
    }
  }

  generateDescription(purchaseOrderLine: PurchaseOrderLine) {
    purchaseOrderLine.lineDescriptionD365 = `${
      purchaseOrderLine.productNameD365 || ""
    }${purchaseOrderLine.colorD365 ? "-" : ""}${
      purchaseOrderLine.colorD365 || ""
    }${purchaseOrderLine.styleD365 ? "-" : ""}${
      purchaseOrderLine.styleD365 || ""
    }${purchaseOrderLine.sizeD365 ? "-" : ""}${
      purchaseOrderLine.sizeD365 || ""
    }`;

    // this.setNetPrice(purchaseOrderLine);
  }
  selectText(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onQuantityKeyDown(
    event: KeyboardEvent,
    purchaseOrderLine: PurchaseOrderLine
  ) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // If backspace or delete is pressed and field will become empty, set to 0
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      (value.length === 1 ||
        (input.selectionStart === 0 && input.selectionEnd === value.length))
    ) {
      event.preventDefault();
      purchaseOrderLine.orderedPurchaseQuantityD365 = 0;
      input.value = "0";
      input.select();
    }
  }

  setNetPrice(purchaseOrderLine: PurchaseOrderLine) {
    const itemNumber = purchaseOrderLine?.itemNumberD365;
    let variants = this.releasedProductsVariantsList.filter(
      x => x.itemIdD365 == itemNumber
    );

    if (purchaseOrderLine?.colorD365) {
      variants = variants.filter(
        x => x.colorD365 == purchaseOrderLine?.colorD365
      );
    }
    if (purchaseOrderLine?.sizeD365) {
      variants = variants.filter(
        x => x.sizeD365 == purchaseOrderLine?.sizeD365
      );
    }
    if (purchaseOrderLine?.styleD365) {
      variants = variants.filter(
        x => x.styleD365 == purchaseOrderLine?.styleD365
      );
    }
    if (purchaseOrderLine?.configD365) {
      variants = variants.filter(
        x => x.configD365 == purchaseOrderLine?.configD365
      );
    }

    this.onNetConversion(
      variants[0]?.costPriceD365?.toString() || "0",
      purchaseOrderLine
    );
  }

  setSizeAndColor(itemNumber: string, purchaseOrderLine: PurchaseOrderLine) {
    this.filteredColors = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort((a, b) => a.colorDisplayOrderD365 - b.colorDisplayOrderD365)
          .map(x => x.colorD365)
      ),
    ];
    this.filteredSizes = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort(
            (a, b) =>
              a.sizeGlobalDisplayOrderD365 - b.sizeGlobalDisplayOrderD365
          )
          .map(x => x.sizeD365)
      ),
    ];
    this.filteredConfig = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort((a, b) => a.configDisplayOrderD365 - b.configDisplayOrderD365)
          .map(x => x.configD365)
      ),
    ];
    this.filteredStyles = [
      ...new Set(
        this.releasedProductsVariantsList
          .filter(x => x.itemIdD365 == itemNumber)
          .sort((a, b) => a.styleDisplayOrderD365 - b.styleDisplayOrderD365)
          .map(x => x.styleD365)
      ),
    ];

    let variants = this.releasedProductsVariantsList.filter(
      x => x.itemIdD365 == itemNumber
    );

    if (purchaseOrderLine?.colorD365) {
      variants = variants.filter(
        x => x.colorD365 == purchaseOrderLine?.colorD365
      );
    }
    if (purchaseOrderLine?.sizeD365) {
      variants = variants.filter(
        x => x.sizeD365 == purchaseOrderLine?.sizeD365
      );
    }
    if (purchaseOrderLine?.styleD365) {
      variants = variants.filter(
        x => x.styleD365 == purchaseOrderLine?.styleD365
      );
    }
    if (purchaseOrderLine?.configD365) {
      variants = variants.filter(
        x => x.configD365 == purchaseOrderLine?.configD365
      );
    }

    purchaseOrderLine.physicalAvailableD365 =
      variants[0]?.physicalAvailableD365;
    purchaseOrderLine.physicalInventoryD365 =
      variants[0]?.physicalInventoryD365;
  }

  onEditRow(portalKey: string, purchaseOrderLine: PurchaseOrderLine) {
    this.cancelAllEditRow();
    const row = this.table.value.filter(x => x.portalKeyD365 === portalKey);
    if (row.length > 0) {
      this.table.initRowEdit(row[0]);
    }

    this.setSizeAndColor(
      purchaseOrderLine.itemNumberD365 ?? "",
      purchaseOrderLine
    );
  }

  onRowEditSave(purchaseOrderLine: PurchaseOrderLine) {
    this.rowSaveEvent.emit(purchaseOrderLine);
  }

  onCopyLines() {
    const selectedPoLines = JSON.parse(JSON.stringify(this.selectedGoods));
    // const selectedPoLines = [...this.selectedGoods];
    // const portalOnlyLines = selectedPoLines.filter((x: any) => !x.id);
    // selectedPoLines.push(...portalOnlyLines);

    if (!selectedPoLines?.length) {
      this._toastr.error("Error", "No lines selected");
      return;
    }

    const ids = this.filteredFinishedGoodsList.map(
      (item: PurchaseOrderLine) => {
        return item.id;
      }
    );
    for (const item of selectedPoLines) {
      item.id = this.findMissingNumber(ids);
      item.portalKeyD365 = uuidv4();
      ids.push(item.id);
    }

    this.filteredFinishedGoodsList.push(...selectedPoLines);
    this.selectedGoods = [];
    this._toastr.success("Succes", "Line(s) Copied Successfully");
  }

  findMissingNumber(arr: number[]) {
    const numSet = new Set(arr);
    let num = 1;

    while (numSet.has(num)) {
      num++;
    }
    return num;
  }

  onRowDelete() {
    const selectedPoLines = [...this.selectedGoods];
    const selectedLinesIdNotInPortal = selectedPoLines.map(
      x => x.portalKeyD365
    );

    this.filteredFinishedGoodsList = this.filteredFinishedGoodsList.filter(
      x => !selectedLinesIdNotInPortal.includes(x.portalKeyD365)
    );

    this.selectedGoods = [];
    this.deleteLinesEvent.emit(selectedPoLines);
    this._toastr.success("Success", "Line(s) Deleted Successfully");
  }

  refreshGrid() {
    // this.gridRefresh.emit();
  }
}
